import { ALL_EQUIPMENTS } from '../../shared/types/oeeRow.ts';
import { OeeRow } from '../types/sharedTypeImpl.ts';


export const OeeGraphValueTypes = [
    { name: 'Availability', value: 'availability' },
    { name: 'Quality', value: 'quality' },
    { name: 'Scraps', value: 'scraps' },
    { name: 'Performance', value: 'performance' },
    { name: 'Utilization', value: 'utilization' },
    { name: 'OEE', value: 'oee' },
    { name: 'OEE without Setup', value: 'oeeWithoutSetup' },
    { name: 'TEEP', value: 'teep' },
    { name: 'Custom', value: 'custom' },
]
export type OeeGraphValueType = typeof OeeGraphValueTypes[number]['value'];

export const OeeComponents = [
    { name: 'Availability', value: 'availability' },
    { name: 'Quality', value: 'quality' },
    { name: 'Performance', value: 'performance' },
    { name: 'Utilization', value: 'utilization' },
    { name: 'Setup', value: 'setup' },
];
export type OeeComponent = typeof OeeComponents[number]['value'];


export function getGraphValueAverage(
    oeeRows: OeeRow[],
    graphValueType: string,
    oeeComponents: OeeComponent[],
) {
    let weightedValueSum = 0;
    let weightSum = 0;
    for (const oeeRow of oeeRows) {
        const { value, weight } = getGraphValue(oeeRow, graphValueType, oeeComponents);
        weightedValueSum += value * weight;
        weightSum += weight;
    }

    const averageValue = weightSum > 0
        ? weightedValueSum / weightSum
        : 0;
    return {
        value: averageValue,
        weight: weightSum,
    }
}

export function getGraphValue(
    oeeRow: OeeRow,
    graphValueType: OeeGraphValueType,
    oeeComponents: OeeComponent[],
) {

    let value: number;
    let weight: number;
    if (graphValueType === 'availability') {
        const shouldBeProducingTime = oeeRow.productionTime + oeeRow.stopTime;
        value = shouldBeProducingTime > 0 ? oeeRow.productionTime / shouldBeProducingTime : 0;
        weight = shouldBeProducingTime;
    } else if (graphValueType === 'quality') {
        value = oeeRow.quality ?? 0;
        weight = oeeRow.equipment === ALL_EQUIPMENTS
            ? oeeRow.productionTime
            : oeeRow.totalPartCount ?? 0;// productionTime can be 0 and part count > 0, when equipment signals don't line up perfectly
    } else if (graphValueType === 'scraps') {
        value = 1 - (oeeRow.quality ?? 1);
        weight = oeeRow.equipment === ALL_EQUIPMENTS
            ? oeeRow.productionTime
            : oeeRow.totalPartCount ?? 0;// productionTime can be 0 and part count > 0, when equipment signals don't line up perfectly
    } else if (graphValueType === 'performance') {
        value = Math.min(oeeRow.performance ?? 0, 1.0);
        weight = oeeRow.productionTime;
    } else if (graphValueType === 'utilization') {
        value = oeeRow.utilization;
        weight = oeeRow.realTime;
    } else if (graphValueType === 'oee') {
        value = oeeRow.oee ?? 0;
        weight = oeeRow.connectedTime;
    } else if (graphValueType === 'oeeWithoutSetup') {
        value = oeeRow.oeeWithoutSetup ?? 0;
        weight = oeeRow.connectedTime - oeeRow.setupTime;
    } else if (graphValueType === 'teep') {
        value = oeeRow.teep;
        weight = oeeRow.realTime;
    } else if (graphValueType === 'custom') {
        if (oeeRow.setupTime && oeeRow.setupTime === oeeRow.connectedTime) {
            value = 0; // If in setup, always return 0
        } else {
            const valueComponents = [
                oeeComponents.includes('availability')
                    ? oeeComponents.includes('setup')
                        ? oeeRow.availability
                        : getAvailabilityWithoutSetup(oeeRow)
                    : null,
                oeeComponents.includes('quality') ? oeeRow.quality : null,
                oeeComponents.includes('performance') ? oeeRow.performance : null,
                oeeComponents.includes('utilization') ? oeeRow.utilization : null,
            ];
            const valueComponentsFiltered = valueComponents.filter(v => v != null);
            value = valueComponentsFiltered.length > 0
                ? valueComponentsFiltered.reduce((acc, v) => acc * (v ?? 1), 1)
                : 0;
            value = Math.min(value, 1.0);
        }

        const weightComponents = [
            oeeRow.productionTime,
            oeeComponents.includes('availability') ? oeeRow.stopTime : null,
            oeeComponents.includes('utilization') ? oeeRow.realTime - oeeRow.connectedTime : null,
        ];
        weight = weightComponents.reduce((acc, v) => acc! + (v ?? 0), 0)!;
    } else {
        throw new Error('Invalid graphValueType: ' + graphValueType);
    }

    return { value, weight };
}

function getAvailabilityWithoutSetup(oeeRow: OeeRow): number | null {
    if (oeeRow.connectedTime === 0) return null;
    if (oeeRow.connectedTime! - oeeRow.setupTime! === 0) return null;
    return oeeRow.productionTime! / (oeeRow.connectedTime! - oeeRow.setupTime!);
}
